import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit, CalendarDays, RefreshCw } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";

export default function HolidaysPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [form, setForm] = useState({ name: "", date: "", is_fixed: true, date_rule: "" });
  const [editForm, setEditForm] = useState({ name: "", is_fixed: true, date_rule: "" });
  const [editDate, setEditDate] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const fetchHolidays = () => {
    setLoading(true);
    API.get("/holidays")
      .then(({ data }) => setHolidays(data || []))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHolidays(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await API.post("/holidays", form);
      toast.success("Holiday created successfully");
      setDialogOpen(false);
      setForm({ name: "", date: "", is_fixed: true, date_rule: "" });
      fetchHolidays();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await API.put(`/holidays/${editDate}`, editForm);
      toast.success("Holiday updated successfully");
      setEditDialogOpen(false);
      fetchHolidays();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (date) => {
    if (!window.confirm(`Delete holiday on ${date}?`)) return;
    try {
      await API.delete(`/holidays/${date}`);
      toast.success("Holiday deleted");
      fetchHolidays();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const handleSeed = async () => {
    if (!window.confirm("This will replace all holidays with the default Kenyan public holidays. Continue?")) return;
    try {
      const { data } = await API.post("/holidays/seed");
      toast.success(data.message);
      fetchHolidays();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const openEdit = (holiday) => {
    setEditDate(holiday.date);
    setEditForm({ name: holiday.name, is_fixed: holiday.is_fixed, date_rule: holiday.date_rule || "" });
    setEditDialogOpen(true);
  };

  const today = new Date().toISOString().split("T")[0];
  const currentYear = new Date().getFullYear().toString();
  const upcoming = holidays.filter((h) => h.date >= today).sort((a, b) => a.date.localeCompare(b.date));
  const past = holidays.filter((h) => h.date < today && h.date.startsWith(currentYear)).sort((a, b) => b.date.localeCompare(a.date));


  return (
    <div className="space-y-6" data-testid="holidays-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl sm:text-3xl tracking-tight font-medium text-slate-950" style={{ fontFamily: "Outfit, sans-serif" }}>
          Kenyan Holidays
        </h1>
        <div className="flex items-center gap-2">
          {isAdmin && (
            <>
              <button
                onClick={handleSeed}
                className="bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" /> Reset Defaults
              </button>
              <button
                onClick={() => setDialogOpen(true)}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-4 py-2.5 font-medium transition-colors text-sm flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> Add Holiday
              </button>
            </>
          )}
        </div>
      </div>

      {/* Upcoming Holidays */}
      <div>
        <h2 className="text-lg font-medium text-slate-900 mb-3 flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-[#002FA7]" />
          Upcoming Holidays ({upcoming.length})
        </h2>
        <div className="border border-slate-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Date</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Observed</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Holiday Name</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Type</TableHead>
                <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Description</TableHead>
                {isAdmin && <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6} className="text-center py-8 text-sm text-slate-500">Loading...</TableCell></TableRow>
              ) : upcoming.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8">

                    <CalendarDays className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                    <p className="text-sm text-slate-500">No upcoming holidays</p>
                  </TableCell>
                </TableRow>
              ) : (
                upcoming.map((h) => (
                  <TableRow key={h.date} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <TableCell className="px-4 py-3">
                      <span className="text-sm font-medium text-slate-900">{h.date}</span>
                    </TableCell>
                    <TableCell className="px-4 py-3 hidden sm:table-cell">
                      <span className="text-sm text-slate-700">{h.observed_date || h.date}</span>
                      {h.observed_date && h.observed_date !== h.date && (
                        <span className="text-xs text-amber-600 block">(weekend rollover)</span>
                      )}
                    </TableCell>
                    <TableCell className="px-4 py-3">
                      <span className="text-sm text-slate-900">{h.name}</span>
                    </TableCell>

                    <TableCell className="px-4 py-3 hidden sm:table-cell">
                      {h.is_fixed ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Fixed</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Variable</Badge>
                      )}
                    </TableCell>
                    <TableCell className="px-4 py-3 hidden md:table-cell text-sm text-slate-500">
                      {h.date_rule || "—"}
                    </TableCell>
                    {isAdmin && (
                      <TableCell className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(h)}
                            className="p-1.5 hover:bg-slate-100 rounded-sm transition-colors text-slate-500 hover:text-[#002FA7]" title="Edit">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleDelete(h.date)}
                            className="p-1.5 hover:bg-red-50 rounded-sm transition-colors text-slate-500 hover:text-red-600" title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Past Holidays */}
      {past.length > 0 && (
        <div>
          <h2 className="text-lg font-medium text-slate-900 mb-3 flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-slate-400" />
            Past Holidays ({past.length})
          </h2>
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Date</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium">Holiday Name</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden sm:table-cell">Type</TableHead>
                  <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium hidden md:table-cell">Description</TableHead>
                  {isAdmin && <TableHead className="px-4 py-3 text-xs uppercase tracking-wider text-slate-500 font-medium text-right">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {past.map((h) => (
                  <TableRow key={h.date} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                    <TableCell className="px-4 py-3 text-sm text-slate-500">{h.date}</TableCell>
                    <TableCell className="px-4 py-3 text-sm text-slate-700">{h.name}</TableCell>
                    <TableCell className="px-4 py-3 hidden sm:table-cell">
                      {h.is_fixed ? (
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">Fixed</Badge>
                      ) : (
                        <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Variable</Badge>
                      )}
                    </TableCell>
                    <TableCell className="px-4 py-3 hidden md:table-cell text-sm text-slate-500">{h.date_rule || "—"}</TableCell>
                    {isAdmin && (
                      <TableCell className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => openEdit(h)}
                            className="p-1.5 hover:bg-slate-100 rounded-sm transition-colors text-slate-500 hover:text-[#002FA7]" title="Edit">
                            <Edit className="h-3.5 w-3.5" />
                          </button>
                          <button onClick={() => handleDelete(h.date)}
                            className="p-1.5 hover:bg-red-50 rounded-sm transition-colors text-slate-500 hover:text-red-600" title="Delete">
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}

      {/* Create Holiday Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New Holiday</DialogTitle>
            <DialogDescription>Add a new public holiday to the calendar.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Holiday Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                  placeholder="e.g. Eid-ul-Fitr"
                  required
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Date</label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                  required
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is-fixed"
                  checked={form.is_fixed}
                  onChange={(e) => setForm({ ...form, is_fixed: e.target.checked })}
                  className="rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]"
                />
                <label htmlFor="is-fixed" className="text-sm text-slate-700">Fixed date (recurring annually)</label>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Date Rule (optional)</label>
                <input
                  type="text"
                  value={form.date_rule}
                  onChange={(e) => setForm({ ...form, date_rule: e.target.value })}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                  placeholder="e.g. 10th Dhul Hijjah (variable)"
                />
                <p className="text-xs text-slate-500 mt-1">Describe how this date is determined (e.g., religious calendar)</p>
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setDialogOpen(false)}
                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
                {submitting ? "Adding..." : "Add Holiday"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Holiday Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Holiday</DialogTitle>
            <DialogDescription>Update holiday details for {editDate}.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit}>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Holiday Name</label>
                <input
                  type="text"
                  value={editForm.name}
                  onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                  required
                />
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="edit-is-fixed"
                  checked={editForm.is_fixed}
                  onChange={(e) => setEditForm({ ...editForm, is_fixed: e.target.checked })}
                  className="rounded border-slate-300 text-[#002FA7] focus:ring-[#002FA7]"
                />
                <label htmlFor="edit-is-fixed" className="text-sm text-slate-700">Fixed date (recurring annually)</label>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500 block mb-1.5">Date Rule</label>
                <input
                  type="text"
                  value={editForm.date_rule}
                  onChange={(e) => setEditForm({ ...editForm, date_rule: e.target.value })}
                  className="bg-white border border-slate-200 rounded-sm px-3 py-2 w-full focus:outline-none focus:ring-2 focus:ring-[#002FA7]/30 focus:border-[#002FA7] transition-all text-sm"
                  placeholder="e.g. 10th Dhul Hijjah (variable)"
                />
              </div>
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setEditDialogOpen(false)}
                className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-900 rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
                Cancel
              </button>
              <button type="submit" disabled={submitting}
                className="bg-[#002FA7] hover:bg-[#002480] text-white rounded-sm px-5 py-2.5 font-medium transition-colors text-sm">
                {submitting ? "Saving..." : "Save Changes"}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
